import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PerformanceMetric {
  id: string;
  employee_id: string;
  metric_period_start: string;
  metric_period_end: string;
  total_bookings: number;
  total_revenue: number;
  conversion_rate: number;
  average_deal_size: number;
  client_satisfaction_score: number;
  response_time_avg_minutes: number;
  followups_completed: number;
  followups_conversion_rate: number;
  tasks_completed: number;
  tasks_on_time_percentage: number;
  avg_task_completion_time_hours: number;
  attendance_percentage: number;
  late_days: number;
  absence_days: number;
  overall_score: number;
  performance_grade: string;
  notes?: string;
  metadata?: Record<string, any>;
  created_at: string;
  employees?: {
    id: string;
    name: string;
  };
}

export const usePerformanceMetrics = (employeeId?: string, startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ["performance-metrics", employeeId, startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from("employee_performance_metrics")
        .select(`
          *,
          employees!employee_performance_metrics_employee_id_fkey(id, name)
        `)
        .order("metric_period_start", { ascending: false });

      if (employeeId) {
        query = query.eq("employee_id", employeeId);
      }

      if (startDate) {
        query = query.gte("metric_period_start", startDate);
      }

      if (endDate) {
        query = query.lte("metric_period_end", endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PerformanceMetric[];
    },
  });
};

export const useCreatePerformanceMetric = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (metric: any) => {
      const { data, error } = await supabase
        .from("employee_performance_metrics")
        .insert([metric])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["performance-metrics"] });
      toast.success("تم حفظ مقاييس الأداء بنجاح");
    },
    onError: (error: any) => {
      toast.error("فشل حفظ مقاييس الأداء: " + error.message);
    },
  });
};

export const useUpdatePerformanceMetric = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PerformanceMetric> }) => {
      const { data: result, error } = await supabase
        .from("employee_performance_metrics")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["performance-metrics"] });
      toast.success("تم تحديث مقاييس الأداء بنجاح");
    },
    onError: (error: any) => {
      toast.error("فشل تحديث مقاييس الأداء: " + error.message);
    },
  });
};